import { useCallback, useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  shippingFeesApi,
  type PreviewInput,
  type PreviewMatch,
  type PreviewResult,
  type RuleInput,
  type RuleSort,
  type RuleStatus,
  type SurchargeRule,
  type ValidateResult,
} from '../../api/shippingFees.js';
import { Modal } from '../../components/Modal.js';
import { EmptyBox, ErrorBox, Field, Pagination, SearchInput, Spinner, StatusBadge } from '../../components/ui.js';
import { useAddressSearch, type DaumAddress } from '../../lib/daumPostcode.js';
import { useUrlSearch, useUrlUpdate } from '../../lib/hooks.js';

/**
 * 바나나몰 랜덤박스 > 지역별배송비 (2026-10-19 요청).
 *
 * 원본: 메두사몰 관리자 https://medusa.cooltrack.jp/manage/shipping-fees ('배송비
 * 설정'). 구성·동작(검사 버튼, 우선순위 ±, 활성 스위치, 테스트 주소 계산)은
 * 원본 그대로 옮겼지만 **메두사몰과 연동하지 않는 별개 시스템**이다(2026-10-19
 * 요청) - 규칙·기본 배송비는 이 시스템의 표(migrations/023)에 있고 계산도 이
 * 서버(modules/shippingFees/region.ts)가 한다. 겉모양은 이 관리자의 스타일을 따른다.
 */

const PAGE_SIZE = 20;
/** 원본과 같은 상한. 서버(shippingFees.routes.ts)도 같은 값으로 막는다. */
const MAX_PRIORITY = 999;
const MAX_BASE_AMOUNT = 1_000_000;

const STATUS_TABS: { key: 'all' | RuleStatus; label: string }[] = [
  { key: 'all', label: '전체' },
  { key: 'active', label: '활성' },
  { key: 'inactive', label: '비활성' },
];

const SORTS: { value: RuleSort; label: string }[] = [
  { value: 'amount', label: '추가 배송비 높은순' },
  { value: 'priority', label: '우선순위순' },
  { value: 'updated', label: '최근 수정순' },
  { value: 'name', label: '이름순' },
];

/** 원본 P() - "5,000원". */
function won(n: number | null | undefined): string {
  if (n === null || n === undefined) return '—';
  return `${n.toLocaleString('ko-KR')}원`;
}

/** 원본 Qc() - 콤마·공백·'원'을 걷어내고 0 이상 정수만 받는다. 아니면 null. */
export function parseWon(raw: string): number | null {
  const s = raw.replace(/[,\s원]/g, '');
  if (!/^\d+$/.test(s)) return null;
  const n = Number(s);
  return Number.isSafeInteger(n) ? n : null;
}

/** 서버의 updated_at(ISO, +09:00)을 이 브라우저 시간대의 'YYYY-MM-DD HH:mm' 로. */
function localDatetime(iso: string | null): string {
  if (!iso) return '-';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
}

type Flash = { tone: 'ok' | 'error'; message: string; warnings?: string[] | undefined };

export function ShippingFeesPage() {
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const update = useUrlUpdate(params, setParams);

  const statusParam = params.get('status');
  const status: 'all' | RuleStatus = statusParam === 'active' || statusParam === 'inactive' ? statusParam : 'all';
  const sortParam = params.get('sort');
  const sort: RuleSort = SORTS.some((s) => s.value === sortParam) ? (sortParam as RuleSort) : 'amount';
  const page = Math.max(1, Number(params.get('page')) || 1);
  const [search, setSearch] = useUrlSearch(params, update);
  const q = params.get('q') ?? '';

  const list = useQuery({
    queryKey: ['shipping-fees', 'rules', q, status, sort, page],
    queryFn: () =>
      shippingFeesApi.listRules({
        q: q || undefined,
        status: status === 'all' ? undefined : status,
        sort,
        limit: PAGE_SIZE,
        offset: (page - 1) * PAGE_SIZE,
      }),
    placeholderData: (prev) => prev,
  });

  const [editor, setEditor] = useState<{ open: boolean; rule: SurchargeRule | null }>({ open: false, rule: null });
  const [deleting, setDeleting] = useState<SurchargeRule | null>(null);
  const [flash, setFlash] = useState<Flash | null>(null);

  const reload = () => void qc.invalidateQueries({ queryKey: ['shipping-fees', 'rules'] });

  // 우선순위 ± 와 활성 스위치 - 표 안에서 바로 바꾼다 (원본과 같다).
  const quick = useMutation({
    mutationFn: (v: { rule: SurchargeRule; patch: Partial<RuleInput>; message: string }) =>
      shippingFeesApi.updateRule(v.rule.id, v.patch),
    onMutate: () => setFlash(null),
    onSuccess: (out, v) => {
      setFlash({ tone: 'ok', message: v.message, warnings: out.warnings });
      reload();
    },
    onError: (err) => setFlash({ tone: 'error', message: err instanceof Error ? err.message : '규칙을 변경하지 못했습니다.' }),
  });
  const busyId = quick.isPending ? quick.variables?.rule.id : null;

  const remove = useMutation({
    mutationFn: (rule: SurchargeRule) => shippingFeesApi.deleteRule(rule.id),
    onSuccess: (_out, rule) => {
      setDeleting(null);
      setFlash({ tone: 'ok', message: `"${rule.name}" 규칙을 삭제했습니다.` });
      reload();
    },
  });

  const changePriority = (rule: SurchargeRule, delta: number) => {
    const next = Math.min(MAX_PRIORITY, Math.max(0, rule.priority + delta));
    quick.mutate({
      rule,
      patch: { priority: next },
      message: `"${rule.name}" 우선순위를 변경했습니다 (${rule.priority} → ${next}).`,
    });
  };

  const toggleActive = (rule: SurchargeRule, on: boolean) =>
    quick.mutate({
      rule,
      patch: { is_active: on },
      message: `"${rule.name}" 규칙을 ${on ? '활성화' : '비활성화'}했습니다.`,
    });

  const data = list.data;
  const rules = data?.rules ?? [];
  const counts = data?.counts;
  const totalPages = data ? Math.max(1, Math.ceil(data.count / PAGE_SIZE)) : 1;
  const filtered = Boolean(q) || status !== 'all';

  return (
    <section className="sfee">
      <header className="page-head">
        <div>
          <h1 className="page-title">지역별배송비</h1>
          <p className="page-desc">기본 배송비와 도서·산간 지역 추가 배송비 규칙을 관리합니다</p>
        </div>
        <div className="page-head__actions">
          <button type="button" className="btn btn--primary" onClick={() => setEditor({ open: true, rule: null })}>
            규칙 추가
          </button>
        </div>
      </header>

      <div className="sfee-howto">
        <p className="sfee-howto__title">배송비 계산 방식</p>
        <ul>
          <li>최종 배송비 = 기본 배송비 + 도서·산간 추가 배송비 (주문당 한 번, 서버가 배송지로 계산)</li>
          <li>
            여러 규칙이 동시에 맞으면 <strong>가장 구체적인 지역 규칙 하나만</strong> 적용합니다(합산하지 않음).
            구체성이 같으면 우선순위가 높은 규칙, 그다음 추가 배송비가 높은 규칙입니다.
          </li>
          <li>무료배송이 적용돼도 도서·산간 추가 배송비는 따로 붙습니다.</li>
          <li>바꾼 설정은 저장하는 즉시 이후 계산에 쓰입니다.</li>
        </ul>
      </div>

      <div className="sfee-grid">
        <BaseFeeCard />
        <PreviewCard />
      </div>

      <h2 className="sfee-section-title">도서·산간 추가 배송비 규칙</h2>

      {flash && (
        <div className={flash.tone === 'ok' ? 'notice notice--ok notice--row' : 'notice notice--warn notice--row'} role="status">
          <div>
            <p className="sfee-flash__msg">{flash.message}</p>
            {flash.warnings?.map((w) => (
              <p key={w} className="sfee-flash__warn">⚠ {w}</p>
            ))}
          </div>
          <button type="button" className="btn btn--ghost btn--sm" aria-label="알림 닫기" onClick={() => setFlash(null)}>
            ✕
          </button>
        </div>
      )}

      <div className="panel">
        <div className="tab-group" aria-label="활성 상태">
          {STATUS_TABS.map((t) => {
            const count = counts ? counts[t.key] : undefined;
            return (
              <button
                key={t.key}
                type="button"
                className={status === t.key ? 'tab-btn tab-btn--active' : 'tab-btn'}
                onClick={() => update({ status: t.key === 'all' ? undefined : t.key })}
              >
                {t.label}
                {count !== undefined ? ` ${count.toLocaleString()}` : ''}
              </button>
            );
          })}
        </div>

        <div className="toolbar sfee-toolbar">
          <select
            className="input"
            aria-label="정렬"
            value={sort}
            onChange={(e) => update({ sort: e.target.value === 'amount' ? undefined : e.target.value }, false)}
          >
            {SORTS.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </select>
          <div className="sfee-toolbar__search">
            <SearchInput value={search} onChange={setSearch} placeholder="지역·규칙 이름 검색 (예: 전라남도, 우도)" />
          </div>
        </div>

        {list.isPending ? (
          <Spinner />
        ) : list.isError && !data ? (
          <ErrorBox error={list.error} onRetry={() => void list.refetch()} />
        ) : rules.length === 0 ? (
          filtered ? (
            <EmptyBox>
              <strong>조건에 맞는 규칙이 없습니다</strong>
              <span>검색어나 상태 필터를 바꿔 보세요.</span>
            </EmptyBox>
          ) : (
            <EmptyBox>
              <strong>등록된 추가 배송비 규칙이 없습니다</strong>
              <span>규칙이 없으면 모든 주소에 기본 배송비만 적용됩니다.</span>
              <button type="button" className="btn btn--primary btn--sm" onClick={() => setEditor({ open: true, rule: null })}>
                규칙 추가
              </button>
            </EmptyBox>
          )
        ) : (
          <div className="table-wrap">
            <table className="table sfee-table">
              <thead>
                <tr>
                  <th>규칙 이름</th>
                  <th>대상 지역</th>
                  <th style={{ textAlign: 'right', width: 110 }}>추가 배송비</th>
                  <th style={{ textAlign: 'center', width: 110 }}>우선순위</th>
                  <th style={{ textAlign: 'center', width: 100 }}>활성</th>
                  <th style={{ width: 130 }}>수정일</th>
                  <th style={{ width: 120 }} aria-label="관리" />
                </tr>
              </thead>
              <tbody>
                {rules.map((r) => {
                  const busy = busyId === r.id;
                  return (
                    <tr key={r.id}>
                      <td>
                        <span className="td-emphasis">{r.name}</span>
                        {r.postal_codes ? <span className="td-sub">우편번호 {r.postal_codes}</span> : null}
                      </td>
                      <td>
                        {r.region_pattern}
                        {r.is_valid ? (
                          <span className="td-sub">
                            {r.normalized_pattern} · {r.depth_label} 단위
                          </span>
                        ) : (
                          <span className="td-sub sfee-invalid">해석할 수 없는 표기 — 적용되지 않습니다. 수정해 주세요.</span>
                        )}
                      </td>
                      <td className="td-nowrap" style={{ textAlign: 'right' }}>
                        <strong>{won(r.amount)}</strong>
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        <span className="sfee-stepper">
                          <button
                            type="button"
                            aria-label="우선순위 낮추기"
                            disabled={busy || r.priority <= 0}
                            onClick={() => changePriority(r, -1)}
                          >
                            −
                          </button>
                          <span className="sfee-stepper__value">{r.priority}</span>
                          <button
                            type="button"
                            aria-label="우선순위 높이기"
                            disabled={busy || r.priority >= MAX_PRIORITY}
                            onClick={() => changePriority(r, 1)}
                          >
                            +
                          </button>
                        </span>
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        <span className="sfee-active">
                          <Switch
                            checked={r.is_active}
                            disabled={busy}
                            label={`${r.name} 활성 상태`}
                            onChange={(on) => toggleActive(r, on)}
                          />
                          <span className={r.is_active ? 'td-ok' : 'td-muted'}>{r.is_active ? '활성' : '꺼짐'}</span>
                        </span>
                      </td>
                      <td className="td-muted td-nowrap">{localDatetime(r.updated_at)}</td>
                      <td style={{ textAlign: 'right' }}>
                        <span className="sfee-actions">
                          <button
                            type="button"
                            className="btn btn--sm"
                            disabled={busy}
                            onClick={() => setEditor({ open: true, rule: r })}
                          >
                            수정
                          </button>
                          <button
                            type="button"
                            className="btn btn--ghost btn--sm sfee-delete"
                            disabled={busy}
                            onClick={() => {
                              remove.reset();
                              setDeleting(r);
                            }}
                          >
                            삭제
                          </button>
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {data && <Pagination page={page} totalPages={totalPages} total={data.count} onChange={(p) => update({ page: p }, false)} />}
      </div>

      {editor.open && (
        <RuleEditor
          rule={editor.rule}
          onClose={() => setEditor({ open: false, rule: null })}
          onSaved={(message, warnings) => {
            setEditor({ open: false, rule: null });
            setFlash({ tone: 'ok', message, warnings });
            reload();
          }}
        />
      )}

      {deleting && (
        <ConfirmDialog
          title="추가 배송비 규칙 삭제"
          message={
            <>
              <p>아래 규칙을 삭제할까요?</p>
              <dl className="deflist sfee-confirm">
                <dt>규칙 이름</dt>
                <dd>{deleting.name}</dd>
                <dt>대상 지역</dt>
                <dd>{deleting.region_pattern}</dd>
                <dt>추가 배송비</dt>
                <dd>
                  <strong>{won(deleting.amount)}</strong>
                </dd>
              </dl>
              <p className="sfee-hint">
                삭제하면 목록에서 사라지고 배송비 계산에 더 이상 쓰이지 않습니다. 잠시 멈추려면 삭제 대신 비활성으로 바꾸세요.
              </p>
            </>
          }
          pending={remove.isPending}
          error={remove.isError ? (remove.error instanceof Error ? remove.error.message : '규칙을 삭제하지 못했습니다.') : null}
          onConfirm={() => remove.mutate(deleting)}
          onClose={() => {
            if (!remove.isPending) setDeleting(null);
          }}
        />
      )}
    </section>
  );
}

/**
 * 확인 모달. components/Modal 의 ConfirmModal 은 본문을 <p> 하나로 감싸서, 원본처럼
 * 표(dl)·목록(ul)을 넣으면 HTML 중첩이 깨진다 - 같은 모양을 <div> 로 다시 짠다.
 */
function ConfirmDialog({
  title,
  message,
  confirmLabel = '삭제',
  danger = true,
  pending,
  error,
  onConfirm,
  onClose,
}: {
  title: string;
  message: ReactNode;
  confirmLabel?: string;
  danger?: boolean;
  pending?: boolean;
  error?: ReactNode;
  onConfirm: () => void;
  onClose: () => void;
}) {
  return (
    <Modal title={title} onClose={onClose}>
      <div className="sfee-confirm-body">{message}</div>
      {error ? (
        <div className="state state--error" role="alert">
          {error}
        </div>
      ) : null}
      <div className="editor__actions">
        <button type="button" className="btn btn--ghost" onClick={onClose} disabled={pending}>
          취소
        </button>
        <button type="button" className={danger ? 'btn btn--danger' : 'btn btn--primary'} onClick={onConfirm} disabled={pending}>
          {pending ? '처리 중…' : confirmLabel}
        </button>
      </div>
    </Modal>
  );
}

function Switch({
  checked,
  disabled,
  label,
  onChange,
}: {
  checked: boolean;
  disabled?: boolean;
  label: string;
  onChange: (next: boolean) => void;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      className={checked ? 'sfee-switch sfee-switch--on' : 'sfee-switch'}
      onClick={() => onChange(!checked)}
    >
      <span className="sfee-switch__knob" />
    </button>
  );
}

/* ── 기본 배송비 ─────────────────────────────────────────────────────────── */

function BaseFeeCard() {
  const qc = useQueryClient();
  const fee = useQuery({ queryKey: ['shipping-fees', 'base-fee'], queryFn: shippingFeesApi.baseFee });
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [fieldError, setFieldError] = useState<string | undefined>();
  const [confirming, setConfirming] = useState<number | null>(null);
  const [done, setDone] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: (amount: number) => shippingFeesApi.setBaseFee(amount),
    onSuccess: (_out, amount) => {
      setDone(`기본 배송비를 ${won(amount)}으로 변경했습니다.`);
      setEditing(false);
      setConfirming(null);
      void qc.invalidateQueries({ queryKey: ['shipping-fees', 'base-fee'] });
    },
    onError: () => setConfirming(null),
  });

  const t = fee.data;

  const startEdit = () => {
    setDraft(t?.base_amount != null ? String(t.base_amount) : '');
    setFieldError(undefined);
    save.reset();
    setDone(null);
    setEditing(true);
  };

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const n = parseWon(draft);
    if (n === null || n < 0 || n > MAX_BASE_AMOUNT) {
      setFieldError(`기본 배송비는 0원 이상 ${won(MAX_BASE_AMOUNT)} 이하의 정수로 입력해 주세요.`);
      return;
    }
    if (n === t?.base_amount) {
      setEditing(false);
      return;
    }
    setConfirming(n);
  };

  return (
    <div className="card sfee-card">
      <div className="card__head">
        <h2 className="card__title">기본 배송비</h2>
        {!editing && t?.base_price_id ? (
          <button type="button" className="btn btn--ghost btn--sm" onClick={startEdit}>
            수정
          </button>
        ) : null}
      </div>

      {fee.isPending ? (
        <Spinner />
      ) : fee.isError && !t ? (
        <ErrorBox error={fee.error} onRetry={() => void fee.refetch()} />
      ) : t ? (
        <div className="sfee-stack">
          {done && <p className="notice notice--ok">{done}</p>}
          {save.isError && <ErrorBox error={save.error} />}

          {editing ? (
            <form onSubmit={submit} className="sfee-stack">
              <Field label="기본 배송비 (원)" error={fieldError}>
                <input
                  className="input"
                  inputMode="numeric"
                  maxLength={12}
                  value={draft}
                  autoFocus
                  onChange={(e) => {
                    setDraft(e.target.value);
                    setFieldError(undefined);
                  }}
                />
              </Field>
              <p className="hint">0원 이상 1,000,000원 이하의 정수</p>
              <div className="editor__actions">
                <button type="button" className="btn btn--ghost btn--sm" onClick={() => setEditing(false)}>
                  취소
                </button>
                <button type="submit" className="btn btn--primary btn--sm">
                  저장
                </button>
              </div>
            </form>
          ) : (
            <div className="sfee-big">
              <span className="td-muted">기본 배송비</span>
              <strong>{won(t.base_amount)}</strong>
            </div>
          )}

          <dl className="sfee-rows">
            <div>
              <dt>무료배송 기준</dt>
              <dd>
                {t.free_threshold != null ? `상품금액 ${won(t.free_threshold)} 이상 무료` : '없음'}
                <StatusBadge tone="warn" label="보호 · 변경 불가" />
              </dd>
            </div>
            <div>
              <dt>적용 배송 옵션</dt>
              <dd>{t.shipping_option_name ?? '—'}</dd>
            </div>
          </dl>

          {!t.base_price_id && (
            <p className="sfee-invalid">표준 배송 옵션에 기본 가격이 없어 여기서 수정할 수 없습니다.</p>
          )}
          <p className="hint">무료배송이 적용돼도 도서·산간 추가 배송비는 따로 붙습니다.</p>
        </div>
      ) : null}

      {confirming !== null && (
        <ConfirmDialog
          title="기본 배송비 변경"
          danger={false}
          confirmLabel="변경"
          pending={save.isPending}
          message={
            <>
              <p>
                기본 배송비를 <strong>{won(t?.base_amount)}</strong> → <strong>{won(confirming)}</strong>으로 변경합니다.
              </p>
              <ul className="sfee-bullets">
                <li>모든 주문의 기본 배송비에 바로 적용됩니다.</li>
                <li>무료배송 기준은 바뀌지 않습니다.</li>
              </ul>
            </>
          }
          onConfirm={() => save.mutate(confirming)}
          onClose={() => {
            if (!save.isPending) setConfirming(null);
          }}
        />
      )}
    </div>
  );
}

/* ── 테스트 주소로 배송비 확인 ───────────────────────────────────────────── */

interface AddressForm {
  province: string;
  city: string;
  address_1: string;
  address_2: string;
  jibun_address: string;
  postal_code: string;
  item_total: string;
}

const EMPTY_ADDRESS: AddressForm = {
  province: '',
  city: '',
  address_1: '',
  address_2: '',
  jibun_address: '',
  postal_code: '',
  item_total: '',
};

const SAMPLES: { label: string; form: Partial<AddressForm> }[] = [
  {
    label: '서울 강남구',
    form: { province: '서울', city: '강남구', address_1: '서울 강남구 테헤란로 152', jibun_address: '서울 강남구 역삼동 737' },
  },
  {
    label: '제주 제주시',
    form: {
      province: '제주특별자치도',
      city: '제주시',
      address_1: '제주특별자치도 제주시 첨단로 242',
      jibun_address: '제주특별자치도 제주시 영평동 2181',
    },
  },
  {
    label: '제주 우도면',
    form: {
      province: '제주특별자치도',
      city: '제주시',
      address_1: '제주특별자치도 제주시 우도면 우도해안길 530',
      jibun_address: '제주특별자치도 제주시 우도면 연평리 2401',
    },
  },
];

/** 다음 우편번호 결과를 원본 화면과 같은 칸으로 나눈다. */
function fromDaum(d: DaumAddress) {
  return {
    province: d.sido ?? '',
    city: d.sigungu ?? '',
    address_1: d.roadAddress || d.address || '',
    jibun_address: d.jibunAddress || d.autoJibunAddress || '',
    postal_code: d.zonecode ?? '',
    building_name: d.buildingName ?? '',
  };
}

function PreviewCard() {
  const [form, setForm] = useState<AddressForm>(EMPTY_ADDRESS);
  const [result, setResult] = useState<PreviewResult | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const postcode = useAddressSearch();

  const preview = useMutation({
    mutationFn: (input: PreviewInput) => shippingFeesApi.preview(input),
    onSuccess: (out) => setResult(out),
    onError: (err) => setFormError(err instanceof Error ? err.message : '배송비를 계산하지 못했습니다.'),
  });

  const set = (key: keyof AddressForm) => (e: { target: { value: string } }) => {
    setForm((f) => ({ ...f, [key]: e.target.value }));
    setFormError(null);
  };

  const applySample = (sample: Partial<AddressForm>) => {
    setForm({ ...EMPTY_ADDRESS, item_total: form.item_total, ...sample });
    setResult(null);
    setFormError(null);
  };

  const findPostcode = () =>
    postcode.open((d) => {
      const a = fromDaum(d);
      setForm((f) => ({
        ...f,
        province: a.province,
        city: a.city,
        address_1: a.address_1,
        jibun_address: a.jibun_address,
        postal_code: a.postal_code,
        address_2: a.building_name || f.address_2,
      }));
      setResult(null);
    });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    let itemTotal: number | undefined;
    if (form.item_total.trim()) {
      const n = parseWon(form.item_total);
      if (n === null) {
        setFormError('상품금액은 0 이상의 정수(원)로 입력해 주세요.');
        return;
      }
      itemTotal = n;
    }
    const input: PreviewInput = {};
    for (const key of ['province', 'city', 'address_1', 'address_2', 'jibun_address', 'postal_code'] as const) {
      const v = form[key].trim();
      if (v) input[key] = v;
    }
    if (itemTotal !== undefined) input.item_total = itemTotal;
    setFormError(null);
    preview.mutate(input);
  };

  return (
    <div className="card sfee-card">
      <h2 className="card__title">테스트 주소로 배송비 확인</h2>
      <p className="hint">실제 배송비 계산과 같은 서버 계산을 사용합니다. 저장되지 않습니다.</p>

      <div className="sfee-samples">
        <button type="button" className="btn btn--sm" disabled={postcode.loading} onClick={findPostcode}>
          {postcode.loading ? '불러오는 중…' : '우편번호 찾기'}
        </button>
        {SAMPLES.map((s) => (
          <button key={s.label} type="button" className="btn btn--ghost btn--sm" onClick={() => applySample(s.form)}>
            {s.label}
          </button>
        ))}
      </div>
      {postcode.error && <p className="sfee-invalid">{postcode.error} 주소를 직접 입력해 주세요.</p>}

      <form className="form-stack" onSubmit={submit}>
        <div className="row-2">
          <Field label="시/도">
            <input className="input" value={form.province} onChange={set('province')} placeholder="예: 제주특별자치도" />
          </Field>
          <Field label="시/군/구">
            <input className="input" value={form.city} onChange={set('city')} placeholder="예: 제주시" />
          </Field>
        </div>
        <Field label="기본주소 (도로명)">
          <input
            className="input"
            value={form.address_1}
            onChange={set('address_1')}
            placeholder="예: 제주특별자치도 제주시 우도면 우도해안길 530"
          />
        </Field>
        <Field label="지번주소">
          <input
            className="input"
            value={form.jibun_address}
            onChange={set('jibun_address')}
            placeholder="예: 제주특별자치도 제주시 우도면 연평리 2401"
          />
          <span className="sfee-hint">리·동 단위 규칙은 지번주소가 있어야 판별됩니다</span>
        </Field>
        <div className="sfee-row-postal">
          <Field label="상세주소">
            <input className="input" value={form.address_2} onChange={set('address_2')} placeholder="섬 이름 등" />
          </Field>
          <Field label="우편번호">
            <input className="input" value={form.postal_code} onChange={set('postal_code')} inputMode="numeric" maxLength={5} />
          </Field>
        </div>
        <Field label="상품금액 (원, 선택)">
          <input
            className="input"
            inputMode="numeric"
            value={form.item_total}
            onChange={set('item_total')}
            maxLength={12}
            placeholder="예: 20000"
          />
          <span className="sfee-hint">넣으면 무료배송 기준까지 반영한 기본 배송비를 보여 줍니다</span>
        </Field>

        {formError && <ErrorBox error={formError} />}

        <div className="editor__actions">
          <button
            type="button"
            className="btn btn--ghost btn--sm"
            onClick={() => {
              setForm(EMPTY_ADDRESS);
              setResult(null);
              setFormError(null);
            }}
          >
            초기화
          </button>
          <button type="submit" className="btn btn--primary btn--sm" disabled={preview.isPending}>
            {preview.isPending ? '계산 중…' : '배송비 계산'}
          </button>
        </div>
      </form>

      {result && <PreviewResultView result={result} />}
    </div>
  );
}

function ResultRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <dt>{label}</dt>
      <dd>{children}</dd>
    </div>
  );
}

function MatchList({ title, matches, inactive = false }: { title: string; matches: PreviewMatch[]; inactive?: boolean }) {
  if (!matches.length) return null;
  return (
    <div>
      <p className="sfee-match-title">{title}</p>
      <ul className="sfee-matches">
        {matches.map((m, i) => (
          <li key={m.rule.id}>
            <span className="sfee-matches__name">
              {!inactive && i === 0 && <StatusBadge tone="info" label="적용" />}
              {inactive && <StatusBadge label="비활성" />}
              <span>{m.rule.name}</span>
              <span className="td-muted">
                {m.rule.depth_label}
                {m.matched_by === 'postal_code' ? ' · 우편번호' : ''}
                {m.rule.priority ? ` · 우선순위 ${m.rule.priority}` : ''}
              </span>
            </span>
            <span className={inactive ? 'td-struck td-muted' : 'td-emphasis'}>{won(m.rule.amount)}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function PreviewResultView({ result: t }: { result: PreviewResult }) {
  const applied = t.applied;
  return (
    <div className="sfee-result">
      {!t.address.complete && (
        <p className="hint hint--warn">
          주소 줄에서 시/도를 읽지 못해 도서·산간 여부를 판별하지 않았습니다. 추가 배송비 없이 기본 배송비만 적용됩니다.
        </p>
      )}
      <dl className="sfee-rows">
        <ResultRow label="읽힌 지역">{t.address.region ?? <span className="td-muted">—</span>}</ResultRow>
        <ResultRow label="적용 규칙">
          {applied ? (
            <span className="sfee-inline">
              {applied.rule.name}
              {applied.rule.depth_label && <StatusBadge label={applied.rule.depth_label} />}
              {applied.matched_by === 'postal_code' && <StatusBadge tone="memo" label="우편번호로 일치" />}
            </span>
          ) : (
            <span className="td-muted">적용되는 규칙 없음</span>
          )}
        </ResultRow>
        <ResultRow label="기본 배송비">
          <span className="sfee-inline">
            {t.free_shipping_applied && <StatusBadge tone="ok" label="무료배송 적용" />}
            {won(t.base_amount)}
          </span>
        </ResultRow>
        <ResultRow label="도서·산간 추가 배송비">
          {t.surcharge_amount > 0 ? `+ ${won(t.surcharge_amount)}` : won(0)}
        </ResultRow>
      </dl>
      <div className="sfee-total">
        <span>최종 배송비</span>
        <strong>{won(t.total_amount)}</strong>
      </div>
      {t.item_total === null && t.free_threshold != null && (
        <p className="hint">상품금액을 넣으면 무료배송 기준(상품금액 {won(t.free_threshold)} 이상)까지 반영합니다.</p>
      )}
      <MatchList
        title={`일치한 규칙 ${t.matches.length}건 — 위에서부터 우선하며 첫 규칙 하나만 적용(합산하지 않음)`}
        matches={t.matches}
      />
      <MatchList title="일치하지만 비활성이라 적용되지 않은 규칙" matches={t.inactive_matches} inactive />
    </div>
  );
}

/* ── 규칙 추가·수정 ──────────────────────────────────────────────────────── */

interface RuleForm {
  name: string;
  region_pattern: string;
  amount: string;
  priority: string;
  postal_codes: string;
  is_active: boolean;
}

const toForm = (r: SurchargeRule | null): RuleForm =>
  r
    ? {
        name: r.name,
        region_pattern: r.region_pattern,
        amount: String(r.amount),
        priority: String(r.priority),
        postal_codes: r.postal_codes ?? '',
        is_active: r.is_active,
      }
    : { name: '', region_pattern: '', amount: '', priority: '0', postal_codes: '', is_active: true };

function RuleEditor({
  rule,
  onClose,
  onSaved,
}: {
  rule: SurchargeRule | null;
  onClose: () => void;
  onSaved: (message: string, warnings?: string[]) => void;
}) {
  const [form, setForm] = useState<RuleForm>(() => toForm(rule));
  const [check, setCheck] = useState<ValidateResult | null>(null);
  const [checking, setChecking] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Modal(useDialogBehavior)은 onClose 가 바뀔 때마다 패널로 초점을 다시 옮긴다.
  // 렌더마다 새 함수를 넘기면 글자 하나 칠 때마다 입력칸에서 초점이 빠진다 -
  // 그래서 최신 값은 ref 로 읽고 함수 자체는 한 번만 만든다.
  const closeRef = useRef(() => {});
  closeRef.current = () => {
    if (!saving) onClose();
  };
  const handleClose = useCallback(() => closeRef.current(), []);

  useEffect(() => {
    setForm(toForm(rule));
    setCheck(null);
    setError(null);
  }, [rule]);

  const set = (key: 'name' | 'region_pattern' | 'amount' | 'priority' | 'postal_codes') =>
    (e: { target: { value: string } }) => {
      setForm((f) => ({ ...f, [key]: e.target.value }));
      setCheck(null);
      setError(null);
    };

  const runCheck = async (): Promise<ValidateResult | null> => {
    setChecking(true);
    setError(null);
    try {
      const out = await shippingFeesApi.validate({
        ...(rule ? { id: rule.id } : {}),
        name: form.name,
        region_pattern: form.region_pattern,
        amount: parseWon(form.amount) ?? form.amount,
        priority: form.priority.trim() === '' ? 0 : (parseWon(form.priority) ?? form.priority),
        is_active: form.is_active,
        postal_codes: form.postal_codes.trim() || null,
      });
      setCheck(out);
      return out;
    } catch (err) {
      setError(err instanceof Error ? err.message : '입력값을 검사하지 못했습니다.');
      return null;
    } finally {
      setChecking(false);
    }
  };

  // 원본과 같다 - 저장은 항상 검사를 먼저 돌리고, 서버가 정리한 값(normalized)으로 보낸다.
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (saving || checking) return;
    const out = await runCheck();
    if (!out?.ok || !out.normalized) return;
    const input: RuleInput = {
      name: form.name.trim(),
      region_pattern: form.region_pattern.trim(),
      amount: out.normalized.amount,
      priority: out.normalized.priority,
      is_active: form.is_active,
      postal_codes: out.normalized.postal_codes,
    };
    setSaving(true);
    try {
      const saved = rule ? await shippingFeesApi.updateRule(rule.id, input) : await shippingFeesApi.createRule(input);
      onSaved(
        rule ? `"${saved.rule.name}" 규칙을 수정했습니다.` : `"${saved.rule.name}" 규칙을 추가했습니다.`,
        saved.warnings,
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : '규칙을 저장하지 못했습니다.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title={rule ? '추가 배송비 규칙 수정' : '추가 배송비 규칙 추가'} wide onClose={handleClose}>
      <form className="form-stack" onSubmit={(e) => void submit(e)}>
        {error && <ErrorBox error={error} />}
        <Field label="규칙 이름">
          <input
            className="input"
            value={form.name}
            onChange={set('name')}
            maxLength={100}
            placeholder="예: 완도 보길도"
            autoFocus
          />
        </Field>
        <Field label="대상 지역">
          <input
            className="input"
            value={form.region_pattern}
            onChange={set('region_pattern')}
            maxLength={100}
            placeholder="예: 전남 완도군 보길면 · 제주특별자치도 전체"
          />
          <span className="sfee-hint">
            시/도 → 시/군/구 → 읍/면/동 → 리 순서로 적습니다. 축약형(전남)과 정식 명칭(전라남도)은 같게 봅니다.
          </span>
        </Field>
        <div className="row-2">
          <Field label="추가 배송비 (원)">
            <input
              className="input"
              inputMode="numeric"
              value={form.amount}
              onChange={set('amount')}
              maxLength={12}
              placeholder="예: 5000"
            />
            <span className="sfee-hint">원 단위 정수</span>
          </Field>
          <Field label={`우선순위 (0~${MAX_PRIORITY})`}>
            <input className="input" inputMode="numeric" value={form.priority} onChange={set('priority')} maxLength={4} />
            <span className="sfee-hint">
              같은 단계의 규칙이 동시에 맞을 때 숫자가 큰 규칙을 먼저 적용합니다. 더 구체적인 지역 규칙이 항상 우선입니다.
            </span>
          </Field>
        </div>
        <Field label="우편번호 (선택)">
          <input className="input" value={form.postal_codes} onChange={set('postal_codes')} maxLength={500} />
          <span className="sfee-hint">예: 63365, 63000-63001 — 주소로 판별되지 않을 때만 보조로 씁니다</span>
        </Field>
        <label className="checkbox">
          <input
            type="checkbox"
            checked={form.is_active}
            onChange={(e) => {
              const on = e.target.checked;
              setForm((f) => ({ ...f, is_active: on }));
              setCheck(null);
            }}
          />
          활성 — 체크를 풀면 저장해도 배송비 계산에 쓰지 않습니다
        </label>

        {check && !check.ok && (
          <div className="state state--error" role="alert">
            <strong>저장할 수 없습니다</strong>
            <ul className="sfee-bullets">
              {check.errors.map((m) => (
                <li key={m}>{m}</li>
              ))}
            </ul>
          </div>
        )}
        {check?.ok && check.normalized && (
          <div className="notice notice--ok">
            <p className="sfee-flash__msg">
              검사 통과 · 비교 기준: <strong>{check.normalized.region_pattern}</strong> · {check.normalized.depth_label} 단위
            </p>
            <p className="sfee-hint">
              추가 배송비 {won(check.normalized.amount)} · 우선순위 {check.normalized.priority}
              {check.normalized.postal_codes ? ` · 우편번호 ${check.normalized.postal_codes}` : ''}
            </p>
          </div>
        )}
        {check && check.warnings.length > 0 && (
          <div className="hint hint--warn">
            <strong>확인해 주세요 (저장은 가능합니다)</strong>
            <ul className="sfee-bullets">
              {check.warnings.map((w) => (
                <li key={w}>{w}</li>
              ))}
            </ul>
          </div>
        )}

        <div className="editor__actions">
          <button type="button" className="btn btn--ghost" onClick={onClose} disabled={saving}>
            취소
          </button>
          <button type="button" className="btn" onClick={() => void runCheck()} disabled={saving || checking}>
            {checking && !saving ? '검사 중…' : '검사'}
          </button>
          <button type="submit" className="btn btn--primary" disabled={saving}>
            {saving ? '저장 중…' : rule ? '저장' : '추가'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
